import { Wand2, Paintbrush, Film, Languages, PenLine, Music, Shuffle, ListChecks } from "lucide-react";
import { useT } from "@/i18n";
import SpeakButton from "@/components/SpeakButton";

const WORDS = [
  { icon: Wand2, color: "bg-violet-500", es: ["Genera", "Pide a la IA que cree algo nuevo."], en: ["Generate", "Ask the AI to make something new."] },
  { icon: Paintbrush, color: "bg-pink-500", es: ["Dibuja / Crea una imagen", "Para pedir dibujos o fotos."], en: ["Draw / Create an image", "To ask for drawings or pictures."] },
  { icon: Film, color: "bg-blue-600", es: ["Anima / Haz un video", "Para que algo se mueva."], en: ["Animate / Make a video", "To make something move."] },
  { icon: Shuffle, color: "bg-orange-500", es: ["Cambia", "Para arreglar algo que ya existe."], en: ["Change", "To fix something that already exists."] },
  { icon: Languages, color: "bg-green-500", es: ["Traduce / Habla en...", "Para usar otro idioma."], en: ["Translate / Speak in...", "To use another language."] },
  { icon: PenLine, color: "bg-cyan-600", es: ["Escribe", "Para cuentos, poemas o anuncios."], en: ["Write", "For stories, poems or ads."] },
  { icon: Music, color: "bg-fuchsia-500", es: ["Agrega música / sonido", "Para videos más divertidos."], en: ["Add music / sound", "For more fun videos."] },
  { icon: ListChecks, color: "bg-amber-500", es: ["Haz una lista", "Para ordenar ideas."], en: ["Make a list", "To organize ideas."] },
];

const RECIPE = {
  es: [["Palabra clave", "Genera un video"], ["¿Qué o quién?", "de un gatito naranja"], ["¿Qué hace?", "que habla en francés"], ["¿Dónde?", "en un jardín con flores"], ["¿Cómo se ve?", "estilo dibujo animado, colores alegres"]],
  en: [["Key word", "Generate a video"], ["What or who?", "of an orange kitten"], ["What does it do?", "that speaks French"], ["Where?", "in a flower garden"], ["How does it look?", "cartoon style, cheerful colors"]],
};

const EXAMPLES = {
  es: [
    "Genera un video donde el gatito hable en francés y diga «Bonjour, mes amis».",
    "Dibuja un dinosaurio amigable comiendo helado en la playa, estilo 3D brillante.",
    "Anima esta imagen: que las nubes se muevan y el sol sonría.",
    "Cambia el fondo de mi foto por un bosque mágico con luciérnagas.",
    "Escribe un anuncio corto y alegre para mi puesto de limonada.",
    "Traduce «¿Quieres jugar conmigo?» al inglés y al portugués.",
  ],
  en: [
    "Generate a video where the kitten speaks French and says “Bonjour, mes amis”.",
    "Draw a friendly dinosaur eating ice cream at the beach, shiny 3D style.",
    "Animate this picture: make the clouds move and the sun smile.",
    "Change my photo's background to a magic forest with fireflies.",
    "Write a short, cheerful ad for my lemonade stand.",
    "Translate “Do you want to play with me?” into Spanish and Portuguese.",
  ],
};

const TX = {
  es: { words: "Palabras mágicas para pedir", recipe: "La receta de una buena instrucción", result: "Todo junto:", examples: "Ejemplos para inspirarte", tip: "Consejo de Spark: sé claro, di qué quieres, cómo se ve y dónde está. ¡Y siempre pide con amabilidad!" },
  en: { words: "Magic words for asking", recipe: "The recipe for a good instruction", result: "All together:", examples: "Examples to inspire you", tip: "Spark's tip: be clear, say what you want, how it looks and where it is. And always ask kindly!" },
};

export default function AskLesson() {
  const { lang } = useT();
  const tx = TX[lang] || TX.es;
  const recipe = RECIPE[lang] || RECIPE.es;
  const full = recipe.map((r) => r[1]).join(" ") + ".";
  return (
    <div className="space-y-6" data-testid="ask-lesson">
      <div>
        <h4 className="font-fredoka text-xl font-bold text-blue-950">{tx.words}</h4>
        <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {WORDS.map((w, i) => {
            const Icon = w.icon; const [t, d] = w[lang] || w.es;
            return (
              <div key={i} className="bg-white rounded-3xl p-3 shadow-md flex items-start gap-3" data-testid={`ask-word-${i}`}>
                <span className={`${w.color} rounded-2xl p-2 shrink-0`}><Icon className="w-5 h-5 text-white" /></span>
                <span><span className="block font-fredoka text-lg font-bold text-blue-950 leading-tight">{t}</span><span className="text-sm font-semibold text-blue-900/80">{d}</span></span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="bg-white rounded-3xl p-4 shadow-md">
        <h4 className="font-fredoka text-xl font-bold text-blue-950">{tx.recipe}</h4>
        <div className="mt-3 flex flex-wrap gap-2">
          {recipe.map(([k, v], i) => (
            <div key={i} className="rounded-2xl bg-sky-50 border-4 border-sky-100 px-3 py-2" data-testid={`ask-recipe-${i}`}>
              <p className="text-xs font-bold uppercase text-blue-700">{i + 1}. {k}</p>
              <p className="font-bold text-blue-950">{v}</p>
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 bg-yellow-100 rounded-2xl px-4 py-3">
          <p className="font-bold text-blue-950"><span className="text-blue-700">{tx.result}</span> {full}</p>
          <SpeakButton compact mascot="spark" text={full} testId="ask-recipe-speak" />
        </div>
      </div>
      <div>
        <h4 className="font-fredoka text-xl font-bold text-blue-950">{tx.examples}</h4>
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          {(EXAMPLES[lang] || EXAMPLES.es).map((ex, i) => (
            <div key={i} className="bg-white rounded-3xl p-4 shadow-md flex items-start justify-between gap-3" data-testid={`ask-example-${i}`}>
              <p className="font-semibold text-blue-950">“{ex}”</p>
              <SpeakButton compact mascot="spark" text={ex} testId={`ask-example-speak-${i}`} />
            </div>
          ))}
        </div>
      </div>
      <p className="bg-blue-800 text-white rounded-3xl px-5 py-3 font-bold" data-testid="ask-tip">{tx.tip}</p>
    </div>
  );
}
